import { useMemo } from "react";
import { AnyTask, Market } from "tm-shared/types";

const isSearchMatch = (market: Market, search: string) => {
    const lowerSearch = search.toLowerCase();
    return (
        market?.contragent?.name?.toLowerCase().includes(lowerSearch) ||
        market?.address?.name?.toLowerCase().includes(lowerSearch)
    );
};

const collator = new Intl.Collator("uk", { sensitivity: "base" });

export const sortMarkets = (
    markets: Market[],
    sortOption: string,
): Market[] => {
    switch (sortOption) {
        case "fop-asc":
            return [...markets].sort((a, b) =>
                collator.compare(a.contragent?.name, b.contragent?.name),
            );
        case "fop-desc":
            return [...markets].sort((a, b) =>
                collator.compare(b.contragent?.name, a.contragent?.name),
            );
        case "address-asc":
            return [...markets].sort((a, b) =>
                collator.compare(a.address?.name, b.address?.name),
            );
        case "address-desc":
            return [...markets].sort((a, b) =>
                collator.compare(b.address?.name, a.address?.name),
            );
        case "route":
        default:
            return markets;
    }
};

export const useFilteredMarkets = (
    markets: Record<string, Market>,
    tasks: Record<string, Record<string, AnyTask>>,
    marketExtraIds: string[],
    searchTerm: string,
    selectedSortOption: { value: string } | null,
) => {
    const isCompleted = (marketId: string) => {
        return Object.values(tasks[marketId] ?? {}).every(
            (task) => task?.completed,
        );
    };

    const isNotCompleted = (marketId: string) => {
        return Object.values(tasks[marketId] ?? {}).some(
            (task) => !task?.completed,
        );
    };

    const filteredMarkets = useMemo(() => {
        const list = Object.values(markets).filter((market) => {
            if (marketExtraIds?.includes(market.id)) return false;
            if (market.unplanned || isCompleted(market.id)) return false;
            return true;
        });

        const searched = searchTerm
            ? list.filter((market) => isSearchMatch(market, searchTerm))
            : list;

        return sortMarkets(searched, selectedSortOption?.value);
    }, [markets, marketExtraIds, searchTerm, selectedSortOption, tasks]);

    const filteredExtraMarkets = useMemo(() => {
        if (!marketExtraIds?.length) return [];

        const list = marketExtraIds
            .map((id) => markets[id])
            .filter((market) => market && isNotCompleted(market.id));

        const searched = searchTerm
            ? list.filter((market) => isSearchMatch(market, searchTerm))
            : list;

        return sortMarkets(searched, selectedSortOption?.value);
    }, [markets, marketExtraIds, searchTerm, selectedSortOption, tasks]);

    const filteredCompletedMarkets = useMemo(() => {
        const list = Object.values(markets).filter((market) => {
            if (market.unplanned && !tasks[market.id]) return false;
            return isCompleted(market.id);
        });

        const searched = searchTerm
            ? list.filter((market) => isSearchMatch(market, searchTerm))
            : list;

        return sortMarkets(searched, selectedSortOption?.value);
    }, [markets, marketExtraIds, searchTerm, selectedSortOption, tasks]);

    return { filteredMarkets, filteredExtraMarkets, filteredCompletedMarkets };
};
